import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Activity,
  Archive,
  CalendarClock,
  ChevronRight,
  CircleUserRound,
  Copy,
  Cpu,
  Database,
  Download,
  FolderOpen,
  Edit3,
  ExternalLink,
  FilePlus,
  FileText,
  Folder,
  FolderPlus,
  HardDrive,
  KeyRound,
  LayoutDashboard,
  ListRestart,
  Loader2,
  Lock,
  LogOut,
  MapPin,
  Network,
  Play,
  RefreshCcw,
  Save,
  Search,
  Server,
  Settings,
  Shield,
  Sparkles,
  Terminal,
  Trash2,
  Unlock,
  Upload,
  Users
} from 'lucide-react';
import { api, apiEnvelope, apiList, apiAllList } from './api';
import { confirmAction } from './dialogs';
import { Modal } from './Modal';
import { AdminServers } from './AdminServers';
import { useAdminRows, AdminSettings, AdminApplicationKeys, AdminMounts, AdminNests } from './AdminResources';
const FileManager = lazy(() => import('./FileManager').then(module => ({ default: module.FileManager })));
const ConsolePage = lazy(() => import('./ConsolePage').then(module => ({ default: module.ConsolePage })));
import './styles.css';
import './parity.css';

type Column<T> = [string, (row: T) => React.ReactNode];

export type User = {
  id: number;
  uuid?: string | null;
  external_id?: string | null;
  admin: boolean;
  root_admin?: boolean;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  language: string;
  '2fa'?: boolean;
  created_at?: string | null;
};

export type Limits = { memory: number; swap: number; disk: number; io: number; cpu: number; threads: string | null; oom_disabled: boolean };
export type FeatureLimits = { databases: number; allocations: number; backups: number };

export type PanelServer = {
  internal_id?: number;
  server_owner?: number;
  allocation?: Allocation;
  sftp_details?: { ip: string; port: number };
  identifier: string;
  uuid: string;
  name: string;
  node: string;
  description: string;
  limits: Limits;
  feature_limits: FeatureLimits;
  status: string | null;
  is_suspended: boolean;
  is_installing: boolean;
  invocation: string;
  docker_image: string;
};

export type ResourceStats = {
  current_state: string;
  is_suspended: boolean;
  resources: {
    memory_bytes: number;
    cpu_absolute: number;
    disk_bytes: number;
    network_rx_bytes: number;
    network_tx_bytes: number;
    uptime: number;
  };
};

export type Allocation = { id: number; ip: string; ip_alias: string | null; alias?: string | null; port: number; notes: string | null; is_default?: boolean; assigned?: boolean };
type ServerDatabase = { id: number; host: { address: string; port: number }; name: string; database?: string; username: string; connections_from?: string; remote?: string; max_connections: number; relationships?: { password?: { attributes?: { password?: string } } } };
type Backup = { uuid: string; name: string; bytes: number; is_successful: boolean; is_locked: boolean; completed_at: string | null };
type ScheduleTask = { id: number; sequence_id: number; action: string; payload: string; time_offset: number; is_queued: boolean; continue_on_failure?: boolean };
type Schedule = {
  id: number;
  name: string;
  cron?: { day_of_week: string; day_of_month: string; hour: string; minute: string; month: string };
  is_active: boolean;
  is_processing: boolean;
  only_when_online: boolean;
  next_run_at: string | null;
  relationships?: { tasks?: { data: Array<{ object: string; attributes: ScheduleTask }> } };
};
type Subuser = { uuid: string; username: string; email: string; permissions: string[]; '2fa_enabled': boolean };
type StartupVariable = { name: string; env_variable: string; server_value: string; default_value: string; is_editable: boolean; rules: string | null };
type ActivityLog = { id: number; event: string; ip: string | null; timestamp: string | null };
type APIKey = { identifier: string; description: string | null; allowed_ips: string[]; last_used_at: string | null; created_at: string | null };
type SSHKey = { name: string; fingerprint: string; public_key: string; created_at: string | null };
type WebsocketCredentials = { token: string; socket: string };

export type ApplicationServer = {
  id: number;
  uuid: string;
  identifier: string;
  external_id: string | null;
  name: string;
  description: string;
  suspended: boolean;
  user: number;
  node: number;
  allocation: number;
  primary_allocation?: Allocation;
  nest: number;
  egg: number;
  skip_scripts?: boolean;
  limits: Limits;
  feature_limits: FeatureLimits;
  container: { startup_command: string; image: string; installed: boolean; environment?: Record<string, string> };
  created_at: string | null;
  updated_at: string | null;
};
export type Node = { behind_proxy?: boolean; memory_overallocate?: number; disk_overallocate?: number; id: number; uuid: string; public: boolean; name: string; description: string | null; location_id: number; fqdn: string; scheme: string; maintenance_mode: boolean; memory: number; disk: number; upload_size: number; daemon_listen: number; daemon_sftp: number; daemon_base: string };
export type Location = { id: number; short: string; long: string | null; created_at: string | null };
export type DatabaseHost = { id: number; name: string; host: string; port: number; username: string; node: number | null; max_databases: number | null };
export type Nest = { id: number; uuid: string; author: string; name: string; description: string | null };

export type AdminData = { users: User[]; servers: ApplicationServer[]; nodes: Node[]; locations: Location[]; databaseHosts: DatabaseHost[]; nests: Nest[] };
type PanelData = { account: User; servers: PanelServer[]; admin?: AdminData };
type Route = { path: string; area: 'client' | 'server' | 'admin' | 'account'; serverId?: string; serverPage?: string; adminPage?: string; accountPage?: string };

async function loadPanelData(): Promise<PanelData> {
  const account = await api<User>('/api/client/account');
  const servers = await apiAllList<PanelServer>('/api/client?per_page=100');
  const data: PanelData = { account, servers };
  if (account.admin) {
    const [users, adminServers, nodes, locations, databaseHosts, nests] = await Promise.allSettled([
      apiAllList<User>('/api/application/users?per_page=100'),
      apiAllList<ApplicationServer>('/api/application/servers?per_page=100'),
      apiAllList<Node>('/api/application/nodes?per_page=100'),
      apiAllList<Location>('/api/application/locations?per_page=100'),
      apiAllList<DatabaseHost>('/api/application/database-hosts?per_page=100'),
      apiAllList<Nest>('/api/application/nests?per_page=100')
    ]);
    data.admin = {
      users: resultOrEmpty(users),
      servers: resultOrEmpty(adminServers),
      nodes: resultOrEmpty(nodes),
      locations: resultOrEmpty(locations),
      databaseHosts: resultOrEmpty(databaseHosts),
      nests: resultOrEmpty(nests)
    };
  }
  return data;
}

function parseRoute(pathname = window.location.pathname): Route {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'server' && parts[1]) return { path: pathname, area: 'server', serverId: parts[1], serverPage: parts[2] ?? 'dashboard' };
  if (parts[0] === 'admin') return { path: pathname, area: 'admin', adminPage: parts[1] === 'api' ? 'application-api' : parts[1] ?? 'overview' };
  if (parts[0] === 'account') return { path: pathname, area: 'account', accountPage: parts[1] ?? 'overview' };
  return { path: pathname, area: 'client' };
}

function navigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function useRoute() {
  const [route, setRoute] = useState(() => parseRoute());
  useEffect(() => {
    const onPop = () => setRoute(parseRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return route;
}

function Login({ onLogin }: { onLogin: (data: PanelData) => void }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [twoFactor, setTwoFactor] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api<User>('/api/artix/login', { method: 'POST', body: JSON.stringify({ login, password, code }) });
      onLogin(await loadPanelData());
      navigate('/');
    } catch (err) {
      if (err instanceof Error && err.message.includes('two-factor')) setTwoFactor(true);
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <form className="login-panel" onSubmit={submit}>
        <div>
          <div className="brand-row"><Shield size={28} /><span>Artix</span></div>
          <h1>Panel Access</h1>
        </div>
        <label>Username or email<input autoComplete="username" value={login} onChange={(event) => setLogin(event.target.value)} required /></label>
        <label>Password<input autoComplete="current-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
        {twoFactor && <label>Authentication or recovery code<input autoFocus autoComplete="one-time-code" value={code} onChange={event => setCode(event.target.value)} required /></label>}
        {error ? <p className="error">{error}</p> : null}
        <button type="submit" disabled={busy}>{busy ? <Loader2 className="spin" size={18} /> : <Terminal size={18} />}{busy ? 'Signing in' : 'Sign in'}</button>
      </form>
    </main>
  );
}

function App() {
  const route = useRoute();
  const [user, setUser] = useState<User | null>(null);
  const [servers, setServers] = useState<PanelServer[]>([]);
  const [admin, setAdmin] = useState<AdminData | undefined>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  function applyPanelData(data: PanelData) {
    setUser(data.account);
    setServers(data.servers);
    setAdmin(data.admin);
    setError('');
  }

  async function refresh() {
    setRefreshing(true);
    setError('');
    try {
      applyPanelData(await loadPanelData());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load panel data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    let alive = true;
    async function bootstrap() {
      try {
        const data = await loadPanelData();
        if (alive) applyPanelData(data);
      } catch {
        if (alive) {
          setUser(null);
          setError('');
        }
      } finally {
        if (alive) setLoading(false);
      }
    }
    bootstrap();
    return () => {
      alive = false;
    };
  }, []);

  async function logout() {
    await fetch('/api/artix/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
    setServers([]);
    setAdmin(undefined);
    setError('');
    navigate('/');
  }

  if (loading) return <main className="loading"><Loader2 className="spin" size={22} /> Loading Artix</main>;
  if (!user) return <Login onLogin={applyPanelData} />;

  const activeServer = route.serverId ? servers.find((server) => server.identifier === route.serverId || server.uuid === route.serverId) : undefined;
  const title = route.area === 'server' && activeServer ? activeServer.name : route.area === 'admin' ? adminTitle(route.adminPage) : route.area === 'account' ? accountTitle(route.accountPage) : 'Dashboard';

  return (
    <main className={`app-shell ${route.area === 'admin' ? 'admin-theme' : 'client-theme'} ${route.area === 'server' ? 'server-area' : ''}`}>
      <TopBar user={user} route={route} servers={servers} onLogout={logout} />
      <section className="workspace">
        <header className="page-heading">
          <div><p>{breadcrumb(route, activeServer)}</p><h1>{title}</h1></div>
          <div className="header-actions">
            <button className="secondary-button" onClick={refresh} disabled={refreshing} title="Refresh data">{refreshing ? <Loader2 className="spin" size={18} /> : <RefreshCcw size={18} />} Refresh</button>
          </div>
        </header>
        {error ? <p className="error">{error}</p> : null}
        <Suspense fallback={<p role="status">Loading...</p>}><RouterView route={route} user={user} servers={servers} activeServer={activeServer} admin={admin} onReload={refresh} /></Suspense>
      </section>
    </main>
  );
}

function TopBar({ user, route, servers, onLogout }: { user: User; route: Route; servers: PanelServer[]; onLogout: () => void }) {
  const [query, setQuery] = useState('');
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return servers.filter((server) => [server.name, server.identifier, server.uuid, server.node].some((value) => value?.toLowerCase().includes(needle))).slice(0, 5);
  }, [query, servers]);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const match = matches[0];
    if (match) {
      setQuery('');
      navigate(`/server/${match.identifier}`);
    }
  }

  return (
    <div className="topbar">
      <button className="topbar-brand" onClick={() => navigate('/')} title="Dashboard"><img src="https://www.elysiacloud.com/elysia.png" width="32" height="32" alt="" /><span>Elysia Cloud</span></button>
      <nav className="topbar-nav">
        <NavButton path="/" label="Servers" icon={<LayoutDashboard size={17} />} active={route.area === 'client'} />
        <NavButton path="/account" label="Account" icon={<CircleUserRound size={17} />} active={route.area === 'account'} />
        {user.admin ? <NavButton path="/admin" label="Admin" icon={<Shield size={17} />} active={route.area === 'admin'} /> : null}
      </nav>
      <form className="topbar-search" onSubmit={submit}>
        <Search size={16} />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search servers or pages..." />
        <kbd>Ctrl + K</kbd>
        {matches.length > 0 ? (
          <div className="search-popover">
            {matches.map((server) => <button key={server.uuid} type="button" onClick={() => { setQuery(''); navigate(`/server/${server.identifier}`); }}>{server.name}<small>{server.identifier} on {server.node}</small></button>)}
          </div>
        ) : null}
      </form>
      <div className="topbar-links">
        <a href="https://discord.com/invite/pzux48qe" target="_blank" rel="noreferrer">Discord</a>
        <a href="https://discord.gg/XMQZ4pH7fr" target="_blank" rel="noreferrer">Support center</a>
      </div>
      <div className="topbar-user">
        {user.admin && <button className="icon-button dark" onClick={() => navigate('/admin')} title="Administration" aria-label="Administration"><Shield size={17} /></button>}
        <button className="user-pill" onClick={() => navigate('/account')} title="Account"><CircleUserRound size={17} /><span>{user.username}</span></button>
        <button className="icon-button dark" onClick={onLogout} title="Sign out"><LogOut size={17} /></button>
      </div>
    </div>
  );
}

function NavButton({ path, label, icon, active }: { path: string; label: string; icon: React.ReactNode; active: boolean }) {
  return <button className={active ? 'active' : ''} onClick={() => navigate(path)} title={label}>{icon}<span>{label}</span></button>;
}

function RouterView(props: { route: Route; user: User; servers: PanelServer[]; activeServer?: PanelServer; admin?: AdminData; onReload: () => Promise<void> }) {
  if (props.route.area === 'account') return <AccountPage user={props.user} page={props.route.accountPage ?? 'overview'} />;
  if (props.route.area === 'server') {
    if (!props.activeServer || !props.route.serverId) return <EmptyState title="Server not found" detail="This server is not available to your account." />;
    return <ServerPage key={props.route.serverId} user={props.user} server={props.activeServer} page={props.route.serverPage ?? 'console'} onReload={props.onReload} />;
  }
  if (props.route.area === 'admin') {
    if (!props.user.admin) return <EmptyState title="Access denied" detail="This account is not an administrator." />;
    if (!props.admin) return <EmptyState title="Admin data unavailable" detail="Refresh the panel and try again." />;
    return <AdminPage page={props.route.adminPage ?? 'overview'} data={props.admin} onReload={props.onReload} />;
  }
  return <Dashboard servers={props.servers} user={props.user} />;
}

function Dashboard({ servers, user }: { servers: PanelServer[]; user: User }) {
  const [stats, setStats] = useState<Record<string, ResourceStats | 'error'>>({});
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(true);
  const folderKey = `artix.server-folders.${user.id}`;
  const [folders, setFolders] = useState<{ name: string; servers: string[] }[]>(() => {
    try { const value = JSON.parse(localStorage.getItem(folderKey) || '[]'); return Array.isArray(value) ? value.filter(row => typeof row.name === 'string' && Array.isArray(row.servers)) : []; } catch { return []; }
  });
  const [folder, setFolder] = useState('');
  const [folderModal, setFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderServers, setFolderServers] = useState<string[]>([]);
  const [folderError, setFolderError] = useState('');
  const displayedServers = useMemo(() => showAll ? servers : servers.filter(server => server.server_owner === user.id), [servers, showAll, user.id]);
  const visibleServers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return displayedServers.filter(server => (!folder || folders.find(row => row.name === folder)?.servers.includes(server.identifier)) && (!needle || [server.name, server.description, server.identifier, server.uuid, server.node].some(value => value?.toLowerCase().includes(needle))));
  }, [query, displayedServers, folder, folders]);

  useEffect(() => {
    let alive = true;
    async function loadStats() {
      const rows = await Promise.allSettled(servers.map((server) => api<ResourceStats>(`/api/client/servers/${server.identifier}/resources`)));
      if (!alive) return;
      const next: Record<string, ResourceStats | 'error'> = {};
      rows.forEach((row, index) => {
        next[servers[index].identifier] = row.status === 'fulfilled' ? row.value : 'error';
      });
      setStats(next);
    }
    loadStats();
    const timer = window.setInterval(loadStats, 30000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [servers]);

  return (
    <>
      <section className="welcome-strip">
        <div>
          <h2>Welcome back</h2>
          <p>Here you can see all the servers you have access to.</p>
        </div>
        <label className="server-toggle"><span>{showAll ? "Showing all servers" : "Showing your servers"}</span><input type="checkbox" checked={showAll} onChange={event => setShowAll(event.target.checked)} /></label>
        <a className="discord-card" href="https://discord.com/invite/pzux48qe" target="_blank" rel="noreferrer"><small>Elysia Cloud Community</small><strong>Join our Discord</strong></a>
      </section>
      <div className="folder-bar"><button className="secondary-button" onClick={() => setFolder('')}>Home</button><span>/ {folder}</span><button className="secondary-button" onClick={() => { setFolder(''); setFolderName(''); setFolderServers([]); setFolderError(''); setFolderModal(true); }}>Create folder +</button>{folder && <button className="secondary-button" onClick={() => { setFolderName(folder); setFolderServers(folders.find(row => row.name === folder)?.servers || []); setFolderError(''); setFolderModal(true); }}>Edit folder</button>}</div>
      {!folder && folders.length > 0 && <div className="folder-bar">{folders.map(row => <button className="secondary-button" key={row.name} onClick={() => setFolder(row.name)}><FolderOpen size={18} />{row.name} ({row.servers.length})</button>)}</div>}
      {folderModal && <Modal title={folder ? 'Edit folder' : 'Create folder'} error={folderError} onClose={() => setFolderModal(false)}><form onSubmit={event => {
        event.preventDefault(); const name = folderName.trim();
        if (!name || folders.some(row => row.name === name && row.name !== folder)) { setFolderError('Choose a unique folder name.'); return; }
        const next = [...folders.filter(row => row.name !== folder), { name, servers: folderServers }];
        try { localStorage.setItem(folderKey, JSON.stringify(next)); setFolders(next); setFolder(name); setFolderModal(false); } catch { setFolderError('Your browser could not save this folder.'); }
      }}><label>Folder name<input required value={folderName} onChange={event => setFolderName(event.target.value)} /></label><p className="muted">Folders organize your server list in this browser.</p>{displayedServers.map(server => <label className="checkbox-row" key={server.identifier}><input type="checkbox" checked={folderServers.includes(server.identifier)} onChange={event => setFolderServers(current => event.target.checked ? [...current, server.identifier] : current.filter(id => id !== server.identifier))} />{server.name}</label>)}<button>Save folder</button></form></Modal>}
      <section className="panel server-index">
        <div className="panel-head">
          <div>
            <h2>All Servers</h2>
            <p className="muted">{visibleServers.length.toLocaleString()} visible of {servers.length.toLocaleString()} total</p>
          </div>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name, UUID, identifier, or node" />
        </div>
        <div className="server-grid">
          {visibleServers.map((server) => <ServerCard key={server.uuid} server={server} stats={stats[server.identifier]} />)}
        </div>
      </section>
      {servers.length === 0 ? <EmptyState title="No servers" detail="No servers are available to this account." /> : null}
    </>
  );
}

function ServerCard({ server, stats }: { server: PanelServer; stats?: ResourceStats | 'error' }) {
  const state = stats && stats !== 'error' ? stats.current_state : server.status ?? 'unknown';
  const cpu = stats && stats !== 'error' ? Math.min(100, Math.max(0, stats.resources.cpu_absolute / Math.max(1, server.limits.cpu || 100) * 100)) : 0;
  const memory = stats && stats !== 'error' && server.limits.memory ? Math.min(100, Math.max(0, stats.resources.memory_bytes / (server.limits.memory * 1024 * 1024) * 100)) : 0;
  const disk = stats && stats !== 'error' && server.limits.disk ? Math.min(100, Math.max(0, stats.resources.disk_bytes / (server.limits.disk * 1024 * 1024) * 100)) : 0;
  const ipLine = connectionAddress(server.allocation);
  return (
    <article className="server-card" tabIndex={0} role="link" onKeyDown={event => { if (event.key === 'Enter') navigate(`/server/${server.identifier}`); }} onClick={() => navigate(`/server/${server.identifier}`)}>
      <div className="server-card-top"><h2>{server.name}</h2><span className={`status ${state}`}>{stats === 'error' ? 'unavailable' : state === 'running' ? 'online' : state}</span></div>
      <dl className="banner-metrics">
        <div><dt>IP</dt><dd>{ipLine}</dd></div>
        <div><dt>CPU</dt><dd>{stats && stats !== 'error' ? `${stats.resources.cpu_absolute.toFixed(2)}%` : '0.00%'} / {server.limits.cpu ? `${server.limits.cpu}%` : 'Unlimited'}</dd></div>
        <div><dt>RAM</dt><dd>{stats && stats !== 'error' ? formatBytes(stats.resources.memory_bytes) : '0 B'} / {server.limits.memory ? formatBytes(server.limits.memory * 1024 * 1024) : 'Unlimited'}</dd></div>
        <div><dt>Disk</dt><dd>{stats && stats !== 'error' ? formatBytes(stats.resources.disk_bytes) : '0 B'} / {server.limits.disk ? formatBytes(server.limits.disk * 1024 * 1024) : 'Unlimited'}</dd></div>
      </dl>
      <div className="usage-bars" aria-hidden="true">
        <span style={{ ['--value' as string]: `${cpu}%` }} />
        <span style={{ ['--value' as string]: `${memory}%` }} />
        <span style={{ ['--value' as string]: `${disk}%` }} />
      </div>
      <button type="button" title="Manage server">Manage server</button>
    </article>
  );
}

function ServerPage({ server, page, onReload, user }: { server: PanelServer; page: string; onReload: () => Promise<void>; user: User }) {
  const [stats, setStats] = useState<ResourceStats | null>(null);
  const [error, setError] = useState('');
  const pages = [
    ['dashboard', 'Dashboard', <LayoutDashboard size={18} />],
    ['console', 'Console', <Terminal size={18} />],
    ['settings', 'Settings', <Settings size={18} />],
    ['activity', 'Activity', <Activity size={18} />],
    ['files', 'Files', <Folder size={18} />],
    ['databases', 'Databases', <Database size={18} />],
    ['backups', 'Backups', <HardDrive size={18} />],
    ['network', 'Network', <Network size={18} />],
    ['schedules', 'Schedules', <CalendarClock size={18} />],
    ['users', 'Users', <Users size={18} />],
    ['startup', 'Startup', <Cpu size={18} />],
  ] as const;

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const row = await api<ResourceStats>(`/api/client/servers/${server.identifier}/resources`);
        if (alive) {
          setStats(row);
          setError('');
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : 'Resource polling unavailable');
      }
    }
    load();
    const timer = window.setInterval(load, 15000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [server.identifier]);

  return (
    <div className="server-layout">
      <aside className="server-sidebar">
        <div className="server-sidebar-head">
          <span className={`status ${stats?.current_state ?? server.status ?? 'offline'}`}>{error ? 'stats unavailable' : stats?.current_state ?? server.status ?? 'offline'}</span>
          <strong>{server.name}</strong>
          <small>{connectionAddress(server.allocation)}</small>
        </div>
        <nav className="side-nav" aria-label="Server navigation">{pages.map(([key, label, icon]) => <React.Fragment key={key}>{['dashboard', 'files', 'schedules'].includes(key) && <span className="server-nav-group">{key === 'dashboard' ? 'General' : key === 'files' ? 'Management' : 'Configuration'}</span>}<button className={page === key || (page === undefined && key === 'dashboard') ? 'active' : ''} onClick={() => navigate(`/server/${server.identifier}${key === 'dashboard' ? '' : `/${key}`}`)}>{icon}<span>{label}</span></button></React.Fragment>)}{user.admin && server.internal_id && <button onClick={() => navigate(`/admin/servers/view/${server.internal_id}`)}><ExternalLink size={18} /><span>Admin view</span></button>}</nav>
      </aside>
      <section className="server-workspace">
        {error && <p className="error" role="alert">{error}</p>}
        <div className="server-title-row">
          <div><p>{server.uuid}</p><h2>{server.name}</h2></div>
          <div className="server-meta">
            <span>{server.node || 'Unassigned node'}</span>
            <span>{server.description || 'No description'}</span>
          </div>
        </div>
        {page === 'dashboard' ? <><ConsolePage server={server} stats={stats} /><ServerDashboardPage server={server} stats={stats} /><SFTPDetails server={server} username={user.username} /></> : null}
        {page === 'console' ? <ConsolePage server={server} stats={stats} /> : null}
        {page === 'files' ? <FileManager serverId={server.identifier} navigate={navigate} /> : null}
        {page === 'databases' ? <DatabasesPage serverId={server.identifier} /> : null}
        {page === 'backups' ? <BackupsPage serverId={server.identifier} /> : null}
        {page === 'schedules' ? <SchedulesPage serverId={server.identifier} /> : null}
        {page === 'network' ? <NetworkPage serverId={server.identifier} /> : null}
        {page === 'startup' ? <StartupPage serverId={server.identifier} server={server} /> : null}
        {page === 'users' ? <SubusersPage serverId={server.identifier} /> : null}
        {page === 'activity' ? <ServerCollectionPage<ActivityLog> path={`/api/client/servers/${server.identifier}/activity?per_page=50`} columns={activityColumns} /> : null}
        {page === 'settings' ? <><SFTPDetails server={server} username={user.username} /><ServerSettingsPage server={server} onReload={onReload} /></> : null}
      </section>
    </div>
  );
}

function ServerDashboardPage({ server, stats }: { server: PanelServer; stats: ResourceStats | null }) {
  const usage = stats?.resources;
  const primaryAddress = connectionAddress(server.allocation);
  const memoryLimit = server.limits.memory ? `${server.limits.memory.toLocaleString()} MiB` : 'Unlimited';
  const diskLimit = server.limits.disk ? `${server.limits.disk.toLocaleString()} MiB` : 'Unlimited';
  const cpuLimit = server.limits.cpu ? `${server.limits.cpu}%` : 'Unlimited';
  return (
    <section className="server-dashboard">
      <div className="server-banner">
        <div>
          <span className={`status ${stats?.current_state ?? server.status ?? 'unknown'}`}>{stats?.current_state ?? server.status ?? 'unknown'}</span>
          <h2>{server.name}</h2>
          <p>{primaryAddress}</p>
        </div>
        <button onClick={() => navigate(`/server/${server.identifier}/console`)}><Terminal size={18} />Open Console</button>
      </div>
      <div className="dashboard-stats">
        <DashboardStat icon={<Cpu size={22} />} label="CPU Usage" value={usage ? `${usage.cpu_absolute.toFixed(2)}%` : 'Unavailable'} limit={cpuLimit} />
        <DashboardStat icon={<HardDrive size={22} />} label="Memory Usage" value={usage ? formatBytes(usage.memory_bytes) : 'Unavailable'} limit={memoryLimit} />
        <DashboardStat icon={<Archive size={22} />} label="Disk Usage" value={usage ? formatBytes(usage.disk_bytes) : 'Unavailable'} limit={diskLimit} />
      </div>
      <div className="dashboard-widgets">
        <section className="panel info-card">
          <PanelHead title="Server Information" action={<button className="icon-button" title="Copy UUID" onClick={() => navigator.clipboard?.writeText(server.uuid)}><Copy size={16} /></button>} />
          <div><span>Node</span><strong>{server.node || 'Unassigned'}</strong></div>
          <div><span>Server ID</span><strong>{server.identifier}</strong></div>
          <div><span>UUID</span><strong>{server.uuid}</strong></div>
        </section>
        <section className="panel info-card">
          <PanelHead title="Quick Actions" />
          <button className="secondary-button" onClick={() => navigate(`/server/${server.identifier}/files`)}><Folder size={18} />Browse Files</button>
          <button className="secondary-button" onClick={() => navigate(`/server/${server.identifier}/databases`)}><Database size={18} />Manage Databases</button>
          <button className="secondary-button" onClick={() => navigate(`/server/${server.identifier}/backups`)}><ExternalLink size={18} />Backups and Restores</button>
        </section>
      </div>
    </section>
  );
}

function DashboardStat({ icon, label, value, limit }: { icon: React.ReactNode; label: string; value: string; limit: string }) {
  return <section className="metric-card"><span>{icon}</span><div><p>{label}</p><strong>{value}</strong><small>/ {limit}</small></div></section>;
}

function DatabasesPage({ serverId }: { serverId: string }) {
  const { rows, loading, error, reload } = useCollection<ServerDatabase>(`/api/client/servers/${serverId}/databases`);
  const [form, setForm] = useState({ database: '', remote: '%', host: 0, max_connections: 0 });
  const [modal, setModal] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');

  async function run(name: string, action: () => Promise<unknown>) {
    setBusy(name);
    setNotice('');
    try {
      await action();
      await reload();
      setModal('');
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Database action failed');
    } finally {
      setBusy('');
    }
  }

  const columns: Column<ServerDatabase>[]= [
    ...databaseColumns,
    ['Password', (row) => databasePassword(row) || 'Hidden'],
    ['Actions', (row) => <div className="table-actions">
      <button onClick={() => run(`rotate-${row.id}`, () => api<unknown>(`/api/client/servers/${serverId}/databases/${row.id}/rotate-password`, { method: 'POST' }))} disabled={!!busy}><RefreshCcw size={16} /> Rotate</button>
      <button className="danger" onClick={async () => await confirmAction(`Delete database ${row.name}?`) && run(`delete-${row.id}`, () => api<unknown>(`/api/client/servers/${serverId}/databases/${row.id}`, { method: 'DELETE' }))} disabled={!!busy}><Trash2 size={16} /> Delete</button>
    </div>]
  ];

  return (
    <section className="panel">
      <PanelHead title="Databases" loading={loading} onReload={reload} action={<button onClick={() => setModal('create')}><Database size={18} /> New Database</button>} />
      {notice ? <p className={notice.includes('failed') || notice.includes('required') ? 'error' : 'notice'}>{notice}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      <DataTableBare rows={rows} columns={columns} />
      {modal === 'create' ? <Modal title="Create Database" error={notice} onClose={() => setModal('')}>
        <form className="modal-form" onSubmit={(event) => {
          event.preventDefault();
          if (!form.database.trim()) return setNotice('Database name is required');
          run('create', () => api<unknown>(`/api/client/servers/${serverId}/databases`, { method: 'POST', body: JSON.stringify(form) }));
        }}>
          <label>Database name<input value={form.database} onChange={(event) => setForm({ ...form, database: event.target.value })} required /></label>
          <label>Connections from<input value={form.remote} onChange={(event) => setForm({ ...form, remote: event.target.value })} required /></label>
          <label>Database host ID<input type="number" min="0" value={form.host} onChange={(event) => setForm({ ...form, host: Number(event.target.value) })} /></label>
          <label>Max connections<input type="number" min="0" value={form.max_connections} onChange={(event) => setForm({ ...form, max_connections: Number(event.target.value) })} /></label>
          <button disabled={!!busy}><Save size={18} /> Create</button>
        </form>
      </Modal> : null}
    </section>
  );
}

function BackupsPage({ serverId }: { serverId: string }) {
  const { rows, loading, error, reload } = useCollection<Backup>(`/api/client/servers/${serverId}/backups`);
  const [form, setForm] = useState({ name: '', ignored: '', is_locked: false });
  const [modal, setModal] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');

  async function run(name: string, action: () => Promise<unknown>) {
    setBusy(name);
    setNotice('');
    try {
      await action();
      await reload();
      setModal('');
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Backup action failed');
    } finally {
      setBusy('');
    }
  }

  async function download(backup: Backup) {
    setBusy(`download-${backup.uuid}`);
    setNotice('');
    try {
      const payload = await api<Record<string, string>>(`/api/client/servers/${serverId}/backups/${backup.uuid}/download`);
      const url = payload.url;
      if (!url) throw new Error('download URL missing');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Could not create download URL');
    } finally {
      setBusy('');
    }
  }

  const columns: Column<Backup>[] = [
    ...backupColumns,
    ['Actions', (row) => <div className="table-actions">
      <button onClick={() => download(row)} disabled={!!busy || !row.is_successful}><Download size={16} /> Download</button>
      <button onClick={async () => await confirmAction(`Restore backup ${row.name}?`) && run(`restore-${row.uuid}`, () => api<unknown>(`/api/client/servers/${serverId}/backups/${row.uuid}/restore`, { method: 'POST', body: JSON.stringify({ truncate: false }) }))} disabled={!!busy}><Upload size={16} /> Restore</button>
      <button onClick={() => run(`lock-${row.uuid}`, () => api<unknown>(`/api/client/servers/${serverId}/backups/${row.uuid}/lock`, { method: 'POST' }))} disabled={!!busy}>{row.is_locked ? <Unlock size={16} /> : <Lock size={16} />}{row.is_locked ? 'Unlock' : 'Lock'}</button>
      <button className="danger" onClick={async () => await confirmAction(`Delete backup ${row.name}?`) && run(`delete-${row.uuid}`, () => api<unknown>(`/api/client/servers/${serverId}/backups/${row.uuid}`, { method: 'DELETE' }))} disabled={!!busy}><Trash2 size={16} /> Delete</button>
    </div>]
  ];

  return (
    <section className="panel">
      <PanelHead title="Backups" loading={loading} onReload={reload} action={<button onClick={() => setModal('create')}><HardDrive size={18} /> New Backup</button>} />
      {notice ? <p className="error">{notice}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      <DataTableBare rows={rows} columns={columns} />
      {modal === 'create' ? <Modal title="Create Backup" error={notice} onClose={() => setModal('')}>
        <form className="modal-form" onSubmit={(event) => {
          event.preventDefault();
          if (!form.name.trim()) return setNotice('Backup name is required');
          run('create', () => api<unknown>(`/api/client/servers/${serverId}/backups`, { method: 'POST', body: JSON.stringify(form) }));
        }}>
          <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></label>
          <label>Ignored files<textarea className="small-textarea" value={form.ignored} onChange={(event) => setForm({ ...form, ignored: event.target.value })} /></label>
          <label className="checkbox-row"><input type="checkbox" checked={form.is_locked} onChange={(event) => setForm({ ...form, is_locked: event.target.checked })} /> Lock backup</label>
          <button disabled={!!busy}><Save size={18} /> Create</button>
        </form>
      </Modal> : null}
    </section>
  );
}

function NetworkPage({ serverId }: { serverId: string }) {
  const { rows, loading, error, reload } = useCollection<Allocation>(`/api/client/servers/${serverId}/network/allocations`);
  const [editing, setEditing] = useState<Allocation | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');

  async function run(name: string, action: () => Promise<unknown>) {
    setBusy(name);
    setNotice('');
    try {
      await action();
      await reload();
      setEditing(null);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Network action failed');
    } finally {
      setBusy('');
    }
  }

  const columns: Column<Allocation>[] = [
    ...allocationColumns,
    ['Actions', (row) => <div className="table-actions">
      <button onClick={() => { setEditing(row); setNotes(row.notes ?? ''); }} disabled={!!busy}><Edit3 size={16} /> Notes</button>
      <button onClick={() => run(`primary-${row.id}`, () => api<unknown>(`/api/client/servers/${serverId}/network/allocations/${row.id}/primary`, { method: 'POST' }))} disabled={!!busy || row.is_default}>Make Primary</button>
      <button className="danger" onClick={async () => await confirmAction(`Unassign allocation ${row.ip}:${row.port}?`) && run(`delete-${row.id}`, () => api<unknown>(`/api/client/servers/${serverId}/network/allocations/${row.id}`, { method: 'DELETE' }))} disabled={!!busy || row.is_default}><Trash2 size={16} /> Delete</button>
    </div>]
  ];

  return (
    <section className="panel">
      <PanelHead title="Network" loading={loading} onReload={reload} action={<button onClick={() => run('assign', () => api<unknown>(`/api/client/servers/${serverId}/network/allocations`, { method: 'POST' }))} disabled={!!busy}><Network size={18} /> Assign Allocation</button>} />
      {notice ? <p className="error">{notice}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      <DataTableBare rows={rows} columns={columns} />
      {editing ? <Modal title="Edit Allocation Notes" error={notice} onClose={() => setEditing(null)}>
        <form className="modal-form" onSubmit={(event) => {
          event.preventDefault();
          run(`notes-${editing.id}`, () => api<unknown>(`/api/client/servers/${serverId}/network/allocations/${editing.id}`, { method: 'POST', body: JSON.stringify({ notes }) }));
        }}>
          <label>Notes<input value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
          <button disabled={!!busy}><Save size={18} /> Save</button>
        </form>
      </Modal> : null}
    </section>
  );
}

function SchedulesPage({ serverId }: { serverId: string }) {
  const { rows, loading, error, reload } = useCollection<Schedule>(`/api/client/servers/${serverId}/schedules`);
  const [modal, setModal] = useState<'schedule' | 'task' | ''>('');
  const [selected, setSelected] = useState<Schedule | null>(null);
  const [scheduleForm, setScheduleForm] = useState({ name: '', minute: '*', hour: '*', day_of_month: '*', month: '*', day_of_week: '*', is_active: true, only_when_online: false });
  const [taskForm, setTaskForm] = useState({ action: 'command', payload: '', time_offset: 0, continue_on_failure: false });
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');

  async function run(name: string, action: () => Promise<unknown>) {
    setBusy(name);
    setNotice('');
    try {
      await action();
      await reload();
      setModal('');
      setSelected(null);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Schedule action failed');
    } finally {
      setBusy('');
    }
  }

  const columns: Column<Schedule>[] = [
    ...scheduleColumns,
    ['Cron', (row) => row.cron ? `${row.cron.minute} ${row.cron.hour} ${row.cron.day_of_month} ${row.cron.month} ${row.cron.day_of_week}` : ''],
    ['Actions', (row) => <div className="table-actions">
      <button onClick={() => run(`execute-${row.id}`, () => api<unknown>(`/api/client/servers/${serverId}/schedules/${row.id}/execute`, { method: 'POST' }))} disabled={!!busy}><Play size={16} /> Run</button>
      <button onClick={() => { setSelected(row); setModal('task'); }} disabled={!!busy}><ListRestart size={16} /> Add Task</button>
      <button className="danger" onClick={async () => await confirmAction(`Delete schedule ${row.name}?`) && run(`delete-${row.id}`, () => api<unknown>(`/api/client/servers/${serverId}/schedules/${row.id}`, { method: 'DELETE' }))} disabled={!!busy}><Trash2 size={16} /> Delete</button>
    </div>]
  ];

  return (
    <section className="panel">
      <PanelHead title="Schedules" loading={loading} onReload={reload} action={<button onClick={() => setModal('schedule')}><CalendarClock size={18} /> New Schedule</button>} />
      {notice ? <p className="error">{notice}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      <DataTableBare rows={rows} columns={columns} />
      <div className="schedule-task-list">
        {rows.map((schedule) => (
          <section className="panel compact-panel" key={schedule.id}>
            <h2>{schedule.name} Tasks</h2>
            <DataTableBare rows={schedule.relationships?.tasks?.data.map((task) => task.attributes) ?? []} columns={[
              ['#', (row) => String(row.sequence_id)],
              ['Action', (row) => row.action],
              ['Payload', (row) => row.payload],
              ['Offset', (row) => `${row.time_offset}s`],
              ['Actions', (row) => <div className="table-actions"><button className="danger" onClick={async () => await confirmAction('Delete this task?') && run(`task-delete-${row.id}`, () => api<unknown>(`/api/client/servers/${serverId}/schedules/${schedule.id}/tasks/${row.id}`, { method: 'DELETE' }))} disabled={!!busy}><Trash2 size={16} /> Delete</button></div>]
            ]} />
          </section>
        ))}
      </div>
      {modal === 'schedule' ? <Modal title="Create Schedule" error={notice} onClose={() => setModal('')}>
        <form className="modal-form" onSubmit={(event) => {
          event.preventDefault();
          if (!scheduleForm.name.trim()) return setNotice('Schedule name is required');
          run('schedule-create', () => api<unknown>(`/api/client/servers/${serverId}/schedules`, { method: 'POST', body: JSON.stringify(scheduleForm) }));
        }}>
          <label>Name<input value={scheduleForm.name} onChange={(event) => setScheduleForm({ ...scheduleForm, name: event.target.value })} required /></label>
          <div className="form-row five">
            <label>Minute<input value={scheduleForm.minute} onChange={(event) => setScheduleForm({ ...scheduleForm, minute: event.target.value })} required /></label>
            <label>Hour<input value={scheduleForm.hour} onChange={(event) => setScheduleForm({ ...scheduleForm, hour: event.target.value })} required /></label>
            <label>Day<input value={scheduleForm.day_of_month} onChange={(event) => setScheduleForm({ ...scheduleForm, day_of_month: event.target.value })} required /></label>
            <label>Month<input value={scheduleForm.month} onChange={(event) => setScheduleForm({ ...scheduleForm, month: event.target.value })} required /></label>
            <label>Weekday<input value={scheduleForm.day_of_week} onChange={(event) => setScheduleForm({ ...scheduleForm, day_of_week: event.target.value })} required /></label>
          </div>
          <label className="checkbox-row"><input type="checkbox" checked={scheduleForm.is_active} onChange={(event) => setScheduleForm({ ...scheduleForm, is_active: event.target.checked })} /> Enabled</label>
          <label className="checkbox-row"><input type="checkbox" checked={scheduleForm.only_when_online} onChange={(event) => setScheduleForm({ ...scheduleForm, only_when_online: event.target.checked })} /> Only when server is online</label>
          <button disabled={!!busy}><Save size={18} /> Create</button>
        </form>
      </Modal> : null}
      {modal === 'task' && selected ? <Modal title={`Add Task to ${selected.name}`} error={notice} onClose={() => { setModal(''); setSelected(null); }}>
        <form className="modal-form" onSubmit={(event) => {
          event.preventDefault();
          if (!taskForm.payload.trim()) return setNotice('Task payload is required');
          run('task-create', () => api<unknown>(`/api/client/servers/${serverId}/schedules/${selected.id}/tasks`, { method: 'POST', body: JSON.stringify(taskForm) }));
        }}>
          <label>Action<select value={taskForm.action} onChange={(event) => setTaskForm({ ...taskForm, action: event.target.value })}><option value="command">Command</option><option value="power">Power</option><option value="backup">Backup</option></select></label>
          <label>Payload<input value={taskForm.payload} onChange={(event) => setTaskForm({ ...taskForm, payload: event.target.value })} required /></label>
          <label>Time offset seconds<input type="number" min="0" value={taskForm.time_offset} onChange={(event) => setTaskForm({ ...taskForm, time_offset: Number(event.target.value) })} /></label>
          <label className="checkbox-row"><input type="checkbox" checked={taskForm.continue_on_failure} onChange={(event) => setTaskForm({ ...taskForm, continue_on_failure: event.target.checked })} /> Continue on failure</label>
          <button disabled={!!busy}><Save size={18} /> Create Task</button>
        </form>
      </Modal> : null}
    </section>
  );
}

function SubusersPage({ serverId }: { serverId: string }) {
  const { rows, loading, error, reload } = useCollection<Subuser>(`/api/client/servers/${serverId}/users`);
  const [form, setForm] = useState({ email: '', permissions: 'control.console\ncontrol.start\ncontrol.stop\ncontrol.restart\nfile.read\nfile.read-content' });
  const [editing, setEditing] = useState<Subuser | null>(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');

  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState<Record<string, string[]>>({});
  useEffect(() => {
    api<{ permissions: Record<string, string[]> }>('/api/client/permissions')
      .then(result => setGroups(result.permissions))
      .catch(err => setNotice(err instanceof Error ? err.message : 'Could not load permissions'));
  }, []);

  async function run(name: string, action: () => Promise<unknown>) {
    setBusy(name);
    setNotice('');
    try {
      await action();
      await reload();
      setEditing(null);
      setOpen(false);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'User action failed');
    } finally {
      setBusy('');
    }
  }

  const columns: Column<Subuser>[] = [
    ...subuserColumns,
    ['Actions', (row) => <div className="table-actions">
      <button onClick={() => { setOpen(true); setNotice(''); setEditing(row); setForm({ email: row.email, permissions: row.permissions.join('\n') }); }} disabled={!!busy}><Edit3 size={16} /> Edit</button>
      <button className="danger" onClick={async () => await confirmAction(`Remove ${row.email} from this server?`) && run(`delete-${row.uuid}`, () => api<unknown>(`/api/client/servers/${serverId}/users/${row.uuid}`, { method: 'DELETE' }))} disabled={!!busy}><Trash2 size={16} /> Delete</button>
    </div>]
  ];

  return (
    <section className="panel">
      <PanelHead title="Users" action={<button onClick={() => { setEditing(null); setForm({ email: '', permissions: 'websocket.connect' }); setNotice(''); setOpen(true); }}><Users size={16} />New User</button>} />
      {!open && notice && <p className="error" role="alert">{notice}</p>}
      {open && <Modal title={editing ? 'Edit User' : 'Invite User'} error={notice} onClose={() => setOpen(false)}>
      <form className="panel" onSubmit={(event) => {
        event.preventDefault();
        if (!form.email.includes('@')) return setNotice('A valid email is required');
        const permissions = form.permissions.split(/\s+/).map((value) => value.trim()).filter(Boolean);
        run(editing ? `update-${editing.uuid}` : 'create', () => api<unknown>(`/api/client/servers/${serverId}/users${editing ? `/${editing.uuid}` : ''}`, { method: 'POST', body: JSON.stringify({ email: form.email, permissions }) }));
      }}>
        <label>Email<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required disabled={!!editing} /></label>
        <div className="permission-groups">{Object.entries(groups).map(([section, permissions]) => <fieldset key={section}><legend>{section}</legend>
          {permissions.map(permission => { const value = section + '.' + permission; return <label className="checkbox-row" key={value}><input type="checkbox" checked={form.permissions.split(/\s+/).includes(value)} onChange={event => {
            const current = form.permissions.split(/\s+/).filter(Boolean);
            setForm({ ...form, permissions: (event.target.checked ? [...current, value] : current.filter(item => item !== value)).join('\n') });
          }} />{permission.replaceAll('-', ' ').replaceAll('_', ' ')}</label>; })}
        </fieldset>)}</div>
        <div className="table-actions">
          <button disabled={!!busy}><Save size={18} /> {editing ? 'Save' : 'Invite'}</button>
          {editing ? <button type="button" className="secondary-button" onClick={() => { setOpen(false); setEditing(null); setForm({ email: '', permissions: '' }); }}>Cancel</button> : null}
        </div>
      </form></Modal>}
      <DataTable rows={rows} columns={columns} loading={loading} error={error} onReload={reload} />
    </section>
  );
}

function ServerCollectionPage<T extends object>({ path, columns }: { path: string; columns: Column<T>[] }) {
  const { rows, loading, error, reload } = useCollection<T>(path);
  return <DataTable rows={rows} columns={columns} loading={loading} error={error} onReload={reload} />;
}

function useCollection<T>(path: string) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  async function reload() {
    setLoading(true);
    setError('');
    try {
      setRows(await apiList<T>(path));
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'Could not load records');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    reload();
  }, [path]);
  return { rows, loading, error, reload };
}

function StartupPage({ serverId, server }: { serverId: string; server: PanelServer }) {
  const { rows, loading, error, reload } = useCollection<StartupVariable>(`/api/client/servers/${serverId}/startup`);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');
  const [saveError, setSaveError] = useState('');
  useEffect(() => {
    setEdits(Object.fromEntries(rows.map((variable) => [variable.env_variable, variable.server_value ?? ''])));
  }, [rows]);

  async function save(variable: StartupVariable) {
    setBusy(variable.env_variable);
    setSaveError('');
    try { await api<unknown>(`/api/client/servers/${serverId}/startup/variable`, { method: 'PUT', body: JSON.stringify({ key: variable.env_variable, value: edits[variable.env_variable] ?? '' }) }); await reload(); }
    catch (err) { setSaveError(err instanceof Error ? err.message : String(err)); }
    finally { setBusy(''); }
  }

  return (
    <section className="panel">
      <PanelHead title="Startup" loading={loading} onReload={reload} />
      <div className="admin-two-columns"><label>Startup Command<textarea readOnly value={server.invocation || ''} /></label><label>Docker Image<input readOnly value={server.docker_image || ''} /></label></div>
      {saveError && <p className="error" role="alert">{saveError}</p>}
      {error ? <p className="error">{error}</p> : null}
      <div className="form-grid">
        {rows.map((variable) => (
          <label key={variable.env_variable}>
            {variable.name}<code>{variable.env_variable}</code>
            <input disabled={!variable.is_editable} value={edits[variable.env_variable] ?? ''} onChange={(event) => setEdits((current) => ({ ...current, [variable.env_variable]: event.target.value }))} />
            <button type="button" disabled={!variable.is_editable || !!busy} onClick={() => save(variable)}><Save size={18} /> Save</button>
          </label>
        ))}
      </div>
    </section>
  );
}

function SFTPDetails({ server, username }: { server: PanelServer; username: string }) {
  const address = server.sftp_details ? `sftp://${server.sftp_details.ip}:${server.sftp_details.port}` : '';
  const login = `${username}.${server.identifier}`;
  return <section className="panel sftp-details"><h2>SFTP Details</h2><div className="admin-two-columns"><label>Server Address<input readOnly value={address || 'Unavailable'} /></label><label>Username<input readOnly value={login} /></label></div><p className="muted">Your SFTP password is the password you use to access this panel.</p>{server.sftp_details && <a href={`sftp://${encodeURIComponent(login)}@${server.sftp_details.ip}:${server.sftp_details.port}`}>Launch SFTP ↗</a>}</section>;
}

function ServerSettingsPage({ server, onReload }: { server: PanelServer; onReload: () => Promise<void> }) {
  const [name, setName] = useState(server.name);
  const [description, setDescription] = useState(server.description);
  const [image, setImage] = useState(server.docker_image);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  async function saveDetails(event: React.FormEvent) {
    event.preventDefault();
    setBusy('details');
    setError('');
    try {
      await api<unknown>(`/api/client/servers/${server.identifier}/settings/rename`, { method: 'POST', body: JSON.stringify({ name, description }) });
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save settings');
    } finally {
      setBusy('');
    }
  }

  async function saveImage(event: React.FormEvent) {
    event.preventDefault();
    setBusy('image');
    setError('');
    try {
      await api<unknown>(`/api/client/servers/${server.identifier}/settings/docker-image`, { method: 'PUT', body: JSON.stringify({ docker_image: image }) });
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save Docker image');
    } finally {
      setBusy('');
    }
  }

  async function reinstall() {
    if (!await confirmAction(`Reinstall ${server.name}? This will rerun the install process through Wings.`)) return;
    setBusy('reinstall');
    setError('');
    try {
      await api<unknown>(`/api/client/servers/${server.identifier}/settings/reinstall`, { method: 'POST' });
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reinstall server');
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="split-panel narrow">
      <form className="panel" onSubmit={saveDetails}>
        <h2><Settings size={18} /> Details</h2>
        <label>Name<input value={name} onChange={(event) => setName(event.target.value)} required /></label>
        <label>Description<input value={description} onChange={(event) => setDescription(event.target.value)} /></label>
        <button disabled={!!busy}><Save size={18} /> Save details</button>
      </form>
      <form className="panel" onSubmit={saveImage}>
        <h2><Cpu size={18} /> Docker Image</h2>
        <label>Image<input value={image} onChange={(event) => setImage(event.target.value)} required /></label>
        <button disabled={!!busy}><Save size={18} /> Save image</button>
        {error ? <p className="error">{error}</p> : null}
      </form>
      <section className="panel">
        <h2><ListRestart size={18} /> Reinstall Server</h2>
        <p className="muted">Rebuild the server container using the current startup and image configuration.</p>
        <button className="danger" disabled={!!busy} onClick={reinstall}><ListRestart size={18} /> Reinstall</button>
      </section>
    </div>
  );
}

function AccountPage({ user, page }: { user: User; page: string }) {
  const [apiKeys, setAPIKeys] = useState<APIKey[]>([]);
  const [sshKeys, setSSHKeys] = useState<SSHKey[]>([]);
  const [activity, setActivity] = useState<ActivityLog[]>([]);
  const [email, setEmail] = useState(user.email);
  const [emailPassword, setEmailPassword] = useState('');
  const [passwords, setPasswords] = useState({ current_password: '', password: '', password_confirmation: '' });
  const [apiForm, setAPIForm] = useState({ description: '', allowed_ips: '' });
  const [sshForm, setSSHForm] = useState({ name: '', public_key: '' });
  const [apiSecret, setAPISecret] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');

  async function reload() {
    const [keys, ssh, rows] = await Promise.allSettled([
      apiList<APIKey>('/api/client/account/api-keys'),
      apiList<SSHKey>('/api/client/account/ssh-keys'),
      apiList<ActivityLog>('/api/client/account/activity?per_page=20')
    ]);
    setAPIKeys(resultOrEmpty(keys));
    setSSHKeys(resultOrEmpty(ssh));
    setActivity(resultOrEmpty(rows));
  }

  useEffect(() => {
    reload();
  }, []);

  async function run(name: string, action: () => Promise<unknown>) {
    setBusy(name);
    setError('');
    setNotice('');
    try {
      await action();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setBusy('');
    }
  }

  const activePage = ['overview', 'api', 'ssh', 'activity'].includes(page) ? page : 'overview';

  return (
    <div className="account-layout">
      <aside className="section-sidebar">
        <div className="section-sidebar-head">
          <strong>Account</strong>
          <small>{user.email}</small>
        </div>
        <nav className="side-nav">
          <NavButton path="/account" label="Overview" icon={<CircleUserRound size={18} />} active={activePage === 'overview'} />
          <NavButton path="/account/api" label="API Credentials" icon={<KeyRound size={18} />} active={activePage === 'api'} />
          <NavButton path="/account/ssh" label="SSH Keys" icon={<Terminal size={18} />} active={activePage === 'ssh'} />
          <NavButton path="/account/activity" label="Activity" icon={<Activity size={18} />} active={activePage === 'activity'} />
        </nav>
      </aside>
      <section className="account-content">
      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="notice">{notice}</p> : null}
      {activePage === 'overview' ? <div className="split-panel narrow">
        <form className="panel" onSubmit={(event) => {
          event.preventDefault();
          run('email', async () => {
            await api<User>('/api/client/account/email', { method: 'PUT', body: JSON.stringify({ email, password: emailPassword }) });
            setEmailPassword('');
            setNotice('Email updated.');
          });
        }}>
          <h2><CircleUserRound size={18} /> Profile</h2>
          <DataTableBare rows={[user]} columns={[
            ['Username', (row) => row.username],
            ['Name', (row) => `${row.first_name} ${row.last_name}`],
            ['Language', (row) => row.language],
            ['Admin', (row) => row.admin ? 'Yes' : 'No']
          ]} />
          <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label>Current password<input type="password" value={emailPassword} onChange={(event) => setEmailPassword(event.target.value)} required /></label>
          <button disabled={!!busy}><Save size={18} /> Update email</button>
        </form>
        <form className="panel" onSubmit={(event) => {
          event.preventDefault();
          run('password', async () => {
            await api<unknown>('/api/client/account/password', { method: 'PUT', body: JSON.stringify(passwords) });
            setPasswords({ current_password: '', password: '', password_confirmation: '' });
            setNotice('Password updated.');
          });
        }}>
          <h2><KeyRound size={18} /> Password</h2>
          <label>Current password<input type="password" value={passwords.current_password} onChange={(event) => setPasswords({ ...passwords, current_password: event.target.value })} required /></label>
          <label>New password<input type="password" value={passwords.password} onChange={(event) => setPasswords({ ...passwords, password: event.target.value })} required /></label>
          <label>Confirm password<input type="password" value={passwords.password_confirmation} onChange={(event) => setPasswords({ ...passwords, password_confirmation: event.target.value })} required /></label>
          <button disabled={!!busy}><Save size={18} /> Update password</button>
        </form>
      </div> : null}
      {activePage === 'api' ? <div className="split-panel narrow">
        <form className="panel" onSubmit={(event) => {
          event.preventDefault();
          run('api-key', async () => {
            const created = await apiEnvelope<APIKey>('/api/client/account/api-keys', {
              method: 'POST',
              body: JSON.stringify({ description: apiForm.description, allowed_ips: apiForm.allowed_ips.split('\n').map((row) => row.trim()).filter(Boolean) })
            });
            setAPISecret(String(created.meta?.secret_token ?? ''));
            setAPIForm({ description: '', allowed_ips: '' });
            setNotice('API key created.');
          });
        }}>
          <h2><KeyRound size={18} /> API Keys</h2>
          {apiSecret ? <p className="notice">Secret token: <code>{apiSecret}</code></p> : null}
          <input placeholder="Description" value={apiForm.description} onChange={(event) => setAPIForm({ ...apiForm, description: event.target.value })} required />
          <textarea className="small-textarea" placeholder="Allowed IPs, one per line" value={apiForm.allowed_ips} onChange={(event) => setAPIForm({ ...apiForm, allowed_ips: event.target.value })} />
          <button disabled={!!busy}>Create key</button>
          <DataTableBare rows={apiKeys} columns={[
            ['Identifier', (row) => row.identifier],
            ['Description', (row) => row.description ?? ''],
            ['Last used', (row) => formatDate(row.last_used_at)],
            ['Actions', (row) => <div className="table-actions"><button className="danger" onClick={async () => await confirmAction(`Delete API key ${row.identifier}?`) && run(`api-delete-${row.identifier}`, () => api<unknown>(`/api/client/account/api-keys/${row.identifier}`, { method: 'DELETE' }))}><Trash2 size={16} /> Delete</button></div>]
          ]} />
        </form>
        <section className="panel">
          <h2><KeyRound size={18} /> API Credentials</h2>
          <p className="muted">Client API keys are compatible with the Pterodactyl API format and can be used by integrations that expect panel credentials.</p>
        </section>
      </div> : null}
      {activePage === 'ssh' ? <div className="split-panel narrow">
        <form className="panel" onSubmit={(event) => {
          event.preventDefault();
          run('ssh-key', async () => {
            await api<SSHKey>('/api/client/account/ssh-keys', { method: 'POST', body: JSON.stringify(sshForm) });
            setSSHForm({ name: '', public_key: '' });
            setNotice('SSH key added.');
          });
        }}>
          <h2><Terminal size={18} /> SSH Keys</h2>
          <input placeholder="Name" value={sshForm.name} onChange={(event) => setSSHForm({ ...sshForm, name: event.target.value })} required />
          <textarea className="small-textarea" placeholder="ssh-ed25519 ..." value={sshForm.public_key} onChange={(event) => setSSHForm({ ...sshForm, public_key: event.target.value })} required />
          <button disabled={!!busy}>Add key</button>
          <DataTableBare rows={sshKeys} columns={[
            ['Name', (row) => row.name],
            ['Fingerprint', (row) => row.fingerprint],
            ['Created', (row) => formatDate(row.created_at)],
            ['Actions', (row) => <div className="table-actions"><button className="danger" onClick={async () => await confirmAction(`Delete SSH key ${row.name}?`) && run(`ssh-delete-${row.fingerprint}`, () => api<unknown>('/api/client/account/ssh-keys/remove', { method: 'POST', body: JSON.stringify({ fingerprint: row.fingerprint }) }))}><Trash2 size={16} /> Delete</button></div>]
          ]} />
        </form>
        <section className="panel">
          <h2><Terminal size={18} /> SFTP</h2>
          <p className="muted">SSH keys are used for passwordless SFTP access through Wings-backed server file management.</p>
        </section>
      </div> : null}
      {activePage === 'activity' ? <DataTable rows={activity} columns={activityColumns} /> : null}
      </section>
    </div>
  );
}

function AdminPage({ page, data, onReload }: { page: string; data: AdminData; onReload: () => Promise<void> }) {
  const totals = {
    memory: data.servers.reduce((sum, server) => sum + server.limits.memory, 0),
    disk: data.servers.reduce((sum, server) => sum + server.limits.disk, 0),
    suspended: data.servers.filter((server) => server.suspended).length,
    maintenance: data.nodes.filter((node) => node.maintenance_mode).length
  };
  const activePage = page || 'overview';
  const adminItems = [
    { group: 'Basic Administration', path: '/admin', label: 'Overview', icon: <LayoutDashboard size={18} />, match: 'overview' },
    { group: 'Basic Administration', path: '/admin/settings', label: 'Settings', icon: <Settings size={18} />, match: 'settings' },
    { group: 'Basic Administration', path: '/admin/arix', label: 'Arix Theme', icon: <Sparkles size={18} />, match: 'arix' },
    { group: 'Basic Administration', path: '/admin/application-api', label: 'Application API', icon: <KeyRound size={18} />, match: 'application-api' },
    { group: 'Management', path: '/admin/databases', label: 'Databases', icon: <Database size={18} />, match: 'databases' },
    { group: 'Management', path: '/admin/locations', label: 'Locations', icon: <MapPin size={18} />, match: 'locations' },
    { group: 'Management', path: '/admin/nodes', label: 'Nodes', icon: <Network size={18} />, match: 'nodes' },
    { group: 'Management', path: '/admin/servers', label: 'Servers', icon: <Server size={18} />, match: 'servers' },
    { group: 'Management', path: '/admin/users', label: 'Users', icon: <Users size={18} />, match: 'users' },
    { group: 'Service Management', path: '/admin/mounts', label: 'Mounts', icon: <HardDrive size={18} />, match: 'mounts' },
    { group: 'Service Management', path: '/admin/nests', label: 'Nests', icon: <Archive size={18} />, match: 'nests' }
  ];
  let content: React.ReactNode;
  if (activePage === 'servers') content = <AdminServers data={data} onReload={onReload} navigate={navigate} />;
  else if (activePage === 'users') content = <AdminUsers data={data} users={data.users} onReload={onReload} />;
  else if (activePage === 'nodes') content = <AdminNodes data={data} nodes={data.nodes} onReload={onReload} />;
  else if (activePage === 'locations') content = <AdminLocations data={data} locations={data.locations} onReload={onReload} />;
  else if (activePage === 'databases') content = <AdminDatabaseHosts data={data} hosts={data.databaseHosts} onReload={onReload} />;
  else if (activePage === 'nests') content = <AdminNests data={data} />;
  else if (activePage === 'settings') content = <AdminSettings />;
  else if (activePage === 'arix') content = <AdminArixTheme />;
  else if (activePage === 'application-api') content = <AdminApplicationKeys />;
  else if (activePage === 'mounts') content = <AdminMounts />;
  else content = <AdminOverview data={data} totals={totals} />;

  return (
    <div className="admin-layout">
      <aside className="section-sidebar admin-sidebar">
        <nav className="side-nav">
          {adminItems.map((item, index) => <React.Fragment key={item.path}>
            {(index === 0 || adminItems[index - 1].group !== item.group) && <span className="admin-nav-heading">{item.group}</span>}
            <NavButton path={item.path} label={item.label} icon={item.icon} active={activePage === item.match || (!page && item.match === 'overview')} />
          </React.Fragment>)}
        </nav>
      </aside>
      <section className="admin-content">
        {!['overview', 'arix'].includes(activePage) && !window.location.pathname.includes('/view/') ? <AdminSectionTitle title={adminTitle(activePage)} page={activePage} /> : null}
        {content}
      </section>
    </div>
  );
}

function AdminSectionTitle({ title, page }: { title: string; page: string }) {
  return <header className="admin-title"><div><h2>{title}</h2><p>Manage {title.toLowerCase()} for the panel.</p></div><span>Admin &gt; {page}</span></header>;
}

function AdminOverview({ data, totals }: { data: AdminData; totals: { memory: number; disk: number; suspended: number; maintenance: number } }) {
  return (
    <section className="admin-overview">
      <header className="admin-title">
        <div><h2>Administrative Overview</h2><p>A quick glance at your system.</p></div>
        <span>Admin &gt; Index</span>
      </header>
      <section className="admin-info-panel">
        <h3>System Information</h3>
        <p>You are running Artix Panel version <code>0.1.0</code>. Your panel compatibility layer is active.</p>
      </section>
      <section className="admin-info-panel">
        <h3>Theme Information</h3>
        <p>You are running the Elysia/Arix frontend theme. The dashboard and admin shell are aligned with the local reference theme.</p>
      </section>
      <div className="admin-quick-links">
        <a className="orange" href="https://discord.com/invite/pzux48qe" target="_blank" rel="noreferrer">Get Help (via Discord)</a>
        <a href="/docs/COMPATIBILITY.md" target="_blank" rel="noreferrer">Documentation</a>
        <a href="https://github.com/valexcloud/artix" target="_blank" rel="noreferrer">Github</a>
        <a className="green" href="https://elysiacloud.com" target="_blank" rel="noreferrer">Support the Project</a>
      </div>
      <div className="metrics admin-metrics">
        <Metric label="Users" value={data.users.length.toLocaleString()} />
        <Metric label="Servers" value={data.servers.length.toLocaleString()} />
        <Metric label="Nodes" value={data.nodes.length.toLocaleString()} />
        <Metric label="Locations" value={data.locations.length.toLocaleString()} />
        <Metric label="Allocated memory" value={`${totals.memory.toLocaleString()} MB`} />
        <Metric label="Allocated disk" value={`${totals.disk.toLocaleString()} MB`} />
        <Metric label="Suspended servers" value={totals.suspended.toLocaleString()} />
        <Metric label="Maintenance nodes" value={totals.maintenance.toLocaleString()} />
      </div>
    </section>
  );
}

function AdminArixTheme() {
  return (
    <section className="admin-overview">
      <header className="admin-title">
        <div><h2>Arix Theme</h2><p>Theme information and access to the shared Arix editor.</p></div>
        <span>Admin &gt; Arix Theme</span>
      </header>
      <section className="admin-info-panel">
        <h3>Theme Information</h3>
        <p>The Artix frontend is using the bundled Elysia/Arix assets, dark sidebar layout, banner server cards, and rounded file manager controls.</p>
      </section>
      <div className="admin-quick-links">
        <a className="orange" href="https://discord.com/invite/pzux48qe" target="_blank" rel="noreferrer">Community Templates</a>
        <a href="https://panel.elysiacloud.com/admin/arix" target="_blank" rel="noreferrer">Open Arix Editor ↗</a>
        <a href="https://panel.elysiacloud.com/admin/arix" target="_blank" rel="noreferrer">Shared Theme Settings ↗</a>
        <a className="green" href="/">Preview Client Area</a>
      </div>
    </section>
  );
}

function AdminPlaceholder({ title, detail }: { title: string; detail: string }) {
  return (
    <section className="panel empty-state">
      <h2>{title}</h2>
      <p>{detail}</p>
    </section>
  );
}

function AdminUsers({ users, onReload, data }: { data: AdminData; users: User[]; onReload: () => Promise<void> }) {
  const { rows: counts, error: countError } = useAdminRows('user-counts');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', first_name: '', last_name: '', password: '', root_admin: false });
  const [editing, setEditing] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function reset() {
    setForm({ username: '', email: '', first_name: '', last_name: '', password: '', root_admin: false });
    setOpen(false); setEditing(null);
  }

  async function saveUser(event: React.FormEvent) {
    event.preventDefault();
    if (!form.username.trim() || !form.email.includes('@') || !form.first_name.trim() || !form.last_name.trim()) {
      setError('Username, valid email, first name, and last name are required');
      return;
    }
    if (!editing && form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api<User>(editing ? `/api/application/users/${editing.id}` : '/api/application/users', { method: editing ? 'PATCH' : 'POST', body: JSON.stringify({ ...form, language: 'en' }) });
      reset();
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save user');
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    const id = Number(window.location.pathname.split('/')[4]);
    const row = users.find(row => row.id === id);
    if (!row) return;
    setEditing(row); setForm({ username: row.username, email: row.email, first_name: row.first_name, last_name: row.last_name, password: '', root_admin: !!(row.admin || row.root_admin) }); setOpen(true);
  }, []);

  const columns: Column<User>[] = [
    ['ID', row => row.id], ['Email', row => row.email], ['Client Name', row => `${row.last_name}, ${row.first_name}`], ['Username', row => row.username], ['2FA', row => row['2fa'] ? 'Enabled' : 'Disabled'], ['Servers Owned', row => data.servers.filter(server => server.user === row.id).length], ['Can Access', row => counts.find(item => Number(item.id) === row.id)?.accessible ?? '—'], ['Administrator', row => row.root_admin || row.admin ? 'Yes' : 'No'],
    ['Actions', (row) => <div className="table-actions">
      <button onClick={() => { setOpen(true); setEditing(row); setForm({ username: row.username, email: row.email, first_name: row.first_name, last_name: row.last_name, password: '', root_admin: !!(row.admin || row.root_admin) }); }} disabled={busy}><Edit3 size={16} /> Edit</button>
      <button className="danger" onClick={async () => {
        if (!await confirmAction(`Delete user ${row.username}? Only use this for transient Artix test users.`)) return;
        setBusy(true);
        setError('');
        try {
          await api<unknown>(`/api/application/users/${row.id}`, { method: 'DELETE' });
          await onReload();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not delete user');
        } finally {
          setBusy(false);
        }
      }} disabled={busy}><Trash2 size={16} /> Delete</button>
    </div>]
  ];
  return (
    <section className="admin-section">
      <PanelHead title="User List" action={<button onClick={() => { setEditing(null); setForm({ username: '', email: '', first_name: '', last_name: '', password: '', root_admin: false }); setError(''); setOpen(true); }}>Create New</button>} />
      {!open && error && <p className="error" role="alert">{error}</p>}
      {open && <Modal title={editing ? 'Edit User' : 'Create User'} onClose={() => setOpen(false)}>
      <form className="panel" onSubmit={saveUser}>

        <input placeholder="Username" value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} required />
        <input placeholder="Email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required />
        <input placeholder="First name" value={form.first_name} onChange={(event) => setForm({ ...form, first_name: event.target.value })} required />
        <input placeholder="Last name" value={form.last_name} onChange={(event) => setForm({ ...form, last_name: event.target.value })} required />
        <input placeholder={editing ? 'New password (optional)' : 'Password'} type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required={!editing} />
        <label className="checkbox-row"><input type="checkbox" checked={form.root_admin} onChange={(event) => setForm({ ...form, root_admin: event.target.checked })} /> Administrator</label>
        {error ? <p className="error">{error}</p> : null}
        <div className="table-actions">
          <button type="submit" disabled={busy}><KeyRound size={18} /> {editing ? 'Save' : 'Create'}</button>
          {editing ? <button type="button" className="secondary-button" onClick={reset}>Cancel</button> : null}
        </div>
      </form></Modal>}
      {countError && <p className="error">Server access counts could not be loaded.</p>}<DataTable rows={users} columns={columns} />
    </section>
  );
}

function AdminLocations({ locations, onReload, data }: { data: AdminData; locations: Location[]; onReload: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ short: '', long: '' });
  const [editing, setEditing] = useState<Location | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form.short.trim()) return setError('Short code is required');
    setBusy(true);
    setError('');
    try {
      await api<Location>(editing ? `/api/application/locations/${editing.id}` : '/api/application/locations', { method: editing ? 'PATCH' : 'POST', body: JSON.stringify(form) });
      setForm({ short: '', long: '' });
      setOpen(false); setEditing(null);
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save location');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    const id = Number(window.location.pathname.split('/')[4]);
    const row = locations.find(row => row.id === id);
    if (!row) return;
    setEditing(row); setForm({ short: row.short, long: row.long || '' }); setOpen(true);
  }, []);

  const columns: Column<Location>[] = [['ID', row => row.id], ['Short Code', row => row.short], ['Description', row => row.long], ['Nodes', row => data.nodes.filter(node => node.location_id === row.id).length], ['Servers', row => data.servers.filter(server => data.nodes.find(node => node.id === server.node)?.location_id === row.id).length], ['Actions', (row) => <div className="table-actions">
    <button disabled={busy} onClick={() => { setOpen(true); setEditing(row); setForm({ short: row.short, long: row.long ?? '' }); }}><Edit3 size={16} /> Edit</button>
    <button className="danger" disabled={busy} onClick={async () => {
      if (!await confirmAction(`Delete location ${row.short}?`)) return;
      setBusy(true);
      setError('');
      try {
        await api<unknown>(`/api/application/locations/${row.id}`, { method: 'DELETE' });
        await onReload();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not delete location');
      } finally {
        setBusy(false);
      }
    }}><Trash2 size={16} /> Delete</button>
  </div>]];

  return (
    <section className="admin-section">
      <PanelHead title="Location List" action={<button onClick={() => { setEditing(null); setForm({ short: '', long: '' }); setError(''); setOpen(true); }}>Create New</button>} />
      {!open && error && <p className="error" role="alert">{error}</p>}
      {open && <Modal title={editing ? 'Edit Location' : 'Create Location'} onClose={() => setOpen(false)}>
      <form className="panel" onSubmit={save}>

        {error ? <p className="error">{error}</p> : null}
        <label>Short code<input value={form.short} onChange={(event) => setForm({ ...form, short: event.target.value })} required /></label>
        <label>Description<input value={form.long} onChange={(event) => setForm({ ...form, long: event.target.value })} /></label>
        <div className="table-actions">
          <button disabled={busy}><Save size={18} /> Save</button>
          {editing ? <button type="button" className="secondary-button" onClick={() => { setOpen(false); setEditing(null); setForm({ short: '', long: '' }); }}>Cancel</button> : null}
        </div>
      </form></Modal>}
      <DataTable rows={locations} columns={columns} />
    </section>
  );
}

function AdminDatabaseHosts({ hosts, onReload, data }: { data: AdminData; hosts: DatabaseHost[]; onReload: () => Promise<void> }) {
  const { rows: counts, error: countError } = useAdminRows('database-counts');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', host: '', port: 3306, username: '', password: '', node: '', max_databases: '' });
  const [editing, setEditing] = useState<DatabaseHost | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form.name.trim() || !form.host.trim() || !form.username.trim() || (!editing && !form.password.trim())) return setError('Name, host, username, and password are required');
    const body = {
      name: form.name,
      host: form.host,
      port: Number(form.port) || 3306,
      username: form.username,
      password: form.password,
      node: form.node ? Number(form.node) : null,
      max_databases: form.max_databases ? Number(form.max_databases) : null
    };
    setBusy(true);
    setError('');
    try {
      await api<DatabaseHost>(editing ? `/api/application/database-hosts/${editing.id}` : '/api/application/database-hosts', { method: editing ? 'PATCH' : 'POST', body: JSON.stringify(body) });
      setForm({ name: '', host: '', port: 3306, username: '', password: '', node: '', max_databases: '' });
      setOpen(false); setEditing(null);
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save database host');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    const id = Number(window.location.pathname.split('/')[4]);
    const row = hosts.find(row => row.id === id);
    if (!row) return;
    setEditing(row); setForm({ name: row.name, host: row.host, port: row.port, username: row.username, password: '', node: row.node == null ? '' : String(row.node), max_databases: row.max_databases == null ? '' : String(row.max_databases) }); setOpen(true);
  }, []);

  const columns: Column<DatabaseHost>[] = [['ID', row => row.id], ['Name', row => row.name], ['Host', row => row.host], ['Port', row => row.port], ['Username', row => row.username], ['Node', row => data.nodes.find(node => node.id === row.node)?.name || 'None'], ['Databases', row => counts.find(item => Number(item.id) === row.id)?.databases ?? '—'], ['Max Databases', row => row.max_databases ?? 'Unlimited'], ['Actions', (row) => <div className="table-actions">
    <button disabled={busy} onClick={() => { setOpen(true); setEditing(row); setForm({ name: row.name, host: row.host, port: row.port, username: row.username, password: '', node: row.node == null ? '' : String(row.node), max_databases: row.max_databases == null ? '' : String(row.max_databases) }); }}><Edit3 size={16} /> Edit</button>
    <button className="danger" disabled={busy} onClick={async () => {
      if (!await confirmAction(`Delete database host ${row.name}?`)) return;
      setBusy(true);
      setError('');
      try {
        await api<unknown>(`/api/application/database-hosts/${row.id}`, { method: 'DELETE' });
        await onReload();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not delete database host');
      } finally {
        setBusy(false);
      }
    }}><Trash2 size={16} /> Delete</button>
  </div>]];

  return (
    <section className="admin-section">
      <PanelHead title="Host List" action={<button onClick={() => { setEditing(null); setForm({ name: '', host: '', port: 3306, username: '', password: '', node: '', max_databases: '' }); setError(''); setOpen(true); }}>Create New</button>} />
      {!open && error && <p className="error" role="alert">{error}</p>}
      {open && <Modal title={editing ? 'Edit Database Host' : 'Create Database Host'} onClose={() => setOpen(false)}>
      <form className="panel" onSubmit={save}>

        {error ? <p className="error">{error}</p> : null}
        <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></label>
        <label>Host<input value={form.host} onChange={(event) => setForm({ ...form, host: event.target.value })} required /></label>
        <label>Port<input type="number" min="1" value={form.port} onChange={(event) => setForm({ ...form, port: Number(event.target.value) })} required /></label>
        <label>Username<input value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} required /></label>
        <label>Password<input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required={!editing} /></label>
        <label>Node ID<input value={form.node} onChange={(event) => setForm({ ...form, node: event.target.value })} /></label>
        <label>Max databases<input value={form.max_databases} onChange={(event) => setForm({ ...form, max_databases: event.target.value })} /></label>
        <div className="table-actions">
          <button disabled={busy}><Save size={18} /> Save</button>
          {editing ? <button type="button" className="secondary-button" onClick={() => { setOpen(false); setEditing(null); setForm({ name: '', host: '', port: 3306, username: '', password: '', node: '', max_databases: '' }); }}>Cancel</button> : null}
        </div>
      </form></Modal>}
      {countError && <p className="error">Database counts could not be loaded.</p>}<DataTable rows={hosts} columns={columns} />
    </section>
  );
}

function AdminNodes({ nodes, onReload, data }: { data: AdminData; nodes: Node[]; onReload: () => Promise<void> }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [config, setConfig] = useState('');
  const [editing, setEditing] = useState<Node | null>(null);
  const [allocating, setAllocating] = useState<Node | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', location_id: 0, fqdn: '', scheme: 'https', behind_proxy: false, public: true, maintenance_mode: false, memory: 0, memory_overallocate: 0, disk: 0, disk_overallocate: 0, upload_size: 100, daemon_base: '/var/lib/pterodactyl/volumes', daemon_sftp: 2022, daemon_listen: 8080 });
  const [allocationForm, setAllocationForm] = useState({ ip: '', alias: '', ports: '' });

  function editNode(node: Node) {
    setOpen(true); setEditing(node);
    setForm({
      name: node.name,
      description: node.description ?? '',
      location_id: node.location_id,
      fqdn: node.fqdn,
      scheme: node.scheme,
      behind_proxy: node.behind_proxy ?? false,
      public: node.public,
      maintenance_mode: node.maintenance_mode,
      memory: node.memory,
      memory_overallocate: node.memory_overallocate ?? 0,
      disk: node.disk,
      disk_overallocate: node.disk_overallocate ?? 0,
      upload_size: node.upload_size,
      daemon_base: node.daemon_base,
      daemon_sftp: node.daemon_sftp,
      daemon_listen: node.daemon_listen
    });
  }

  async function saveNode(event: React.FormEvent) {
    event.preventDefault();
    if (!form.name.trim() || !form.fqdn.trim() || !form.location_id || !form.memory || !form.disk) {
      setError('Name, FQDN, location, memory, and disk are required');
      return;
    }
    setBusy('node-save');
    setError('');
    try {
      await api<Node>(editing ? `/api/application/nodes/${editing.id}` : '/api/application/nodes', { method: editing ? 'PATCH' : 'POST', body: JSON.stringify(form) });
      setOpen(false); setEditing(null);
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save node');
    } finally {
      setBusy('');
    }
  }

  useEffect(() => {
    const id = Number(window.location.pathname.split('/')[4]);
    const row = nodes.find(row => row.id === id);
    if (!row) return;
    if (window.location.pathname.endsWith('/allocation')) { setAllocating(row); setAllocationForm({ ip: '', alias: '', ports: '' }); }
    else editNode(row);
  }, []);

  const columns: Column<Node>[] = [['Name', row => row.name], ['Location', row => data.locations.find(location => location.id === row.location_id)?.short || row.location_id], ['Memory', row => `${row.memory.toLocaleString()} MiB`], ['Disk', row => `${row.disk.toLocaleString()} MiB`], ['Servers', row => data.servers.filter(server => server.node === row.id).length], ['SSL', row => row.scheme === 'https' ? 'Yes' : 'No'], ['Public', row => row.public ? 'Yes' : 'No'], ['Actions', (row) => <div className="table-actions">
    <button disabled={!!busy} onClick={() => editNode(row)}><Edit3 size={16} /> Edit</button>
    <button disabled={!!busy} onClick={() => { setAllocating(row); setAllocationForm({ ip: '', alias: '', ports: '' }); }}><Network size={16} /> Allocations</button>
    <button disabled={!!busy} onClick={async () => {
      setBusy(`config-${row.id}`);
      setError('');
      try {
        const payload = await api<Record<string, unknown>>(`/api/application/nodes/${row.id}/configuration`);
        setConfig(JSON.stringify(payload, null, 2));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load configuration');
      } finally {
        setBusy('');
      }
    }}><FileText size={16} /> Config</button>
    <button className="danger" disabled={!!busy} onClick={async () => {
      if (!await confirmAction(`Delete node ${row.name}? Nodes with servers cannot be deleted.`)) return;
      setBusy(`delete-${row.id}`);
      setError('');
      try {
        await api<unknown>(`/api/application/nodes/${row.id}`, { method: 'DELETE' });
        await onReload();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not delete node');
      } finally {
        setBusy('');
      }
    }}><Trash2 size={16} /> Delete</button>
  </div>]];
  return (
    <section className="admin-section">
      <PanelHead title="Node List" action={<button onClick={() => { setEditing(null); setForm({ name: '', description: '', location_id: 0, fqdn: '', scheme: 'https', behind_proxy: false, public: true, maintenance_mode: false, memory: 0, memory_overallocate: 0, disk: 0, disk_overallocate: 0, upload_size: 100, daemon_base: '/var/lib/pterodactyl/volumes', daemon_sftp: 2022, daemon_listen: 8080 }); setError(''); setOpen(true); }}>Create New</button>} />
      {!open && error && <p className="error" role="alert">{error}</p>}
      {open && <Modal title={editing ? 'Edit Node' : 'Create Node'} onClose={() => setOpen(false)}>
      <form className="panel" onSubmit={saveNode}>

      {error ? <p className="error">{error}</p> : null}
        <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></label>
        <label>FQDN<input value={form.fqdn} onChange={(event) => setForm({ ...form, fqdn: event.target.value })} required /></label>
        <div className="form-row">
          <label>Location ID<input type="number" min="1" value={form.location_id} onChange={(event) => setForm({ ...form, location_id: Number(event.target.value) })} required /></label>
          <label>Scheme<select value={form.scheme} onChange={(event) => setForm({ ...form, scheme: event.target.value })}><option value="https">https</option><option value="http">http</option></select></label>
        </div>
        <div className="form-row">
          <label>Memory MB<input type="number" min="1" value={form.memory} onChange={(event) => setForm({ ...form, memory: Number(event.target.value) })} required /></label>
          <label>Disk MB<input type="number" min="1" value={form.disk} onChange={(event) => setForm({ ...form, disk: Number(event.target.value) })} required /></label>
        </div>
        <div className="form-row">
          <label>Daemon Listen<input type="number" min="1" value={form.daemon_listen} onChange={(event) => setForm({ ...form, daemon_listen: Number(event.target.value) })} /></label>
          <label>SFTP Port<input type="number" min="1" value={form.daemon_sftp} onChange={(event) => setForm({ ...form, daemon_sftp: Number(event.target.value) })} /></label>
        </div>
        <div className="form-row"><label>Memory Overallocation (%)<input type="number" min="-1" value={form.memory_overallocate} onChange={event => setForm({ ...form, memory_overallocate: Number(event.target.value) })} /></label><label>Disk Overallocation (%)<input type="number" min="-1" value={form.disk_overallocate} onChange={event => setForm({ ...form, disk_overallocate: Number(event.target.value) })} /></label></div>
        <label>Maximum Upload Size (MiB)<input type="number" min="1" value={form.upload_size} onChange={event => setForm({ ...form, upload_size: Number(event.target.value) })} /></label>
        <label className="checkbox-row"><input type="checkbox" checked={form.behind_proxy} onChange={event => setForm({ ...form, behind_proxy: event.target.checked })} /> Behind a proxy</label>
        <label>Daemon Base<input value={form.daemon_base} onChange={(event) => setForm({ ...form, daemon_base: event.target.value })} /></label>
        <label>Description<input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
        <label className="checkbox-row"><input type="checkbox" checked={form.public} onChange={(event) => setForm({ ...form, public: event.target.checked })} /> Public</label>
        <label className="checkbox-row"><input type="checkbox" checked={form.maintenance_mode} onChange={(event) => setForm({ ...form, maintenance_mode: event.target.checked })} /> Maintenance mode</label>
        <div className="table-actions">
          <button disabled={!!busy}><Save size={18} /> Save</button>
          {editing ? <button type="button" className="secondary-button" onClick={() => { setEditing(null); setOpen(false); }}>Cancel</button> : null}
        </div>
      </form></Modal>}
      <div className="admin-content">
      {config ? <section className="panel"><PanelHead title="Node Configuration" action={<button className="secondary-button" onClick={() => setConfig('')}>Close</button>} /><textarea value={config} readOnly spellCheck={false} /></section> : null}
      <DataTable rows={nodes} columns={columns} />
      </div>
      {allocating ? <Modal title={`Create Allocations on ${allocating.name}`} error={error} onClose={() => setAllocating(null)}>
        <form className="modal-form" onSubmit={async (event) => {
          event.preventDefault();
          if (!allocationForm.ip.trim() || !allocationForm.ports.trim()) return setError('IP and ports are required');
          setBusy(`alloc-${allocating.id}`);
          setError('');
          try {
            await api<unknown>(`/api/application/nodes/${allocating.id}/allocations`, { method: 'POST', body: JSON.stringify({ ip: allocationForm.ip, alias: allocationForm.alias, ports: allocationForm.ports.split(/\s+/).filter(Boolean) }) });
            setAllocating(null);
            await onReload();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not create allocations');
          } finally {
            setBusy('');
          }
        }}>
          <label>IP<input value={allocationForm.ip} onChange={(event) => setAllocationForm({ ...allocationForm, ip: event.target.value })} required /></label>
          <label>Alias<input value={allocationForm.alias} onChange={(event) => setAllocationForm({ ...allocationForm, alias: event.target.value })} /></label>
          <label>Ports<textarea className="small-textarea" value={allocationForm.ports} onChange={(event) => setAllocationForm({ ...allocationForm, ports: event.target.value })} required /></label>
          <button disabled={!!busy}><Save size={18} /> Create Allocations</button>
        </form>
      </Modal> : null}
    </section>
  );
}

function DataTable<T extends object>({ rows, columns, loading, error, onReload }: { rows: T[]; columns: Column<T>[]; loading?: boolean; error?: string; onReload?: () => void }) {
  return (
    <section className="panel">
      <PanelHead title="Records" loading={loading} onReload={onReload} />
      {error ? <p className="error">{error}</p> : null}
      <DataTableBare rows={rows} columns={columns} />
    </section>
  );
}

function DataTableBare<T extends object>({ rows, columns, onRowClick }: { rows: T[]; columns: Column<T>[]; onRowClick?: (row: T) => void }) {
  return (
    <div className="table-wrap">
      <table>
        <thead><tr>{columns.map(([label]) => <th key={label}>{label}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={columns.length}>No records found.</td></tr> : rows.map((row, index) => (
            <tr key={index} onClick={() => onRowClick?.(row)} className={onRowClick ? 'clickable-row' : ''}>{columns.map(([label, render]) => <td key={label}>{render(row)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PanelHead({ title, loading, onReload, action }: { title: string; loading?: boolean; onReload?: () => void; action?: React.ReactNode }) {
  return <div className="panel-head"><h2>{title}</h2><div className="table-actions">{action}{onReload ? <button className="secondary-button" onClick={onReload} disabled={loading}>{loading ? <Loader2 className="spin" size={18} /> : <RefreshCcw size={18} />} Reload</button> : null}</div></div>;
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div><span>{value}</span><p>{label}</p></div>;
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
  return <section className="empty-state"><h2>{title}</h2><p>{detail}</p></section>;
}

function resultOrEmpty<T>(result: PromiseSettledResult<T[]>): T[] {
  return result.status === 'fulfilled' ? result.value : [];
}

function databasePassword(row: ServerDatabase) {
  return row.relationships?.password?.attributes?.password ?? '';
}

function fileActionTitle(action: string) {
  const titles: Record<string, string> = {
    folder: 'Create Folder',
    file: 'Create File',
    rename: 'Rename',
    copy: 'Copy',
    chmod: 'Change Permissions',
    pull: 'Pull Remote File'
  };
  return titles[action] ?? 'File Action';
}

const databaseColumns: Column<ServerDatabase>[] = [['Name', (row) => row.name ?? row.database ?? ''], ['Username', (row) => row.username], ['Host', (row) => row.host ? `${row.host.address}:${row.host.port}` : ''], ['Remote', (row) => row.connections_from ?? row.remote ?? ''], ['Max', (row) => String(row.max_connections)]];
const backupColumns: Column<Backup>[] = [['Name', (row) => row.name], ['UUID', (row) => row.uuid], ['Size', (row) => formatBytes(row.bytes)], ['State', (row) => row.is_successful ? 'Successful' : 'Pending/failed'], ['Locked', (row) => row.is_locked ? 'Yes' : 'No']];
const scheduleColumns: Column<Schedule>[] = [['Name', (row) => row.name], ['State', (row) => row.is_processing ? 'Processing' : row.is_active ? 'Active' : 'Inactive'], ['Online only', (row) => row.only_when_online ? 'Yes' : 'No'], ['Next run', (row) => formatDate(row.next_run_at)], ['Tasks', (row) => String(row.relationships?.tasks?.data?.length ?? 0)]];
const allocationColumns: Column<Allocation>[] = [['Address', (row) => `${row.ip_alias ?? row.ip}:${row.port}`], ['IP', (row) => row.ip], ['Alias', (row) => row.ip_alias ?? ''], ['Notes', (row) => row.notes ?? ''], ['Default', (row) => row.is_default ? 'Yes' : 'No']];
const subuserColumns: Column<Subuser>[] = [['User', (row) => row.username], ['Email', (row) => row.email], ['2FA', (row) => row['2fa_enabled'] ? 'Enabled' : 'Disabled'], ['Permissions', (row) => row.permissions.join(', ')]];
const activityColumns: Column<ActivityLog>[] = [['Event', (row) => row.event], ['IP', (row) => row.ip ?? ''], ['Time', (row) => formatDate(row.timestamp)]];
const userColumns: Column<User>[] = [['ID', (row) => String(row.id)], ['Username', (row) => row.username], ['Email', (row) => row.email], ['Name', (row) => `${row.first_name} ${row.last_name}`], ['Admin', (row) => row.admin || row.root_admin ? 'Yes' : 'No'], ['2FA', (row) => row['2fa'] ? 'Enabled' : 'Disabled']];
const nodeColumns: Column<Node>[] = [['ID', (row) => String(row.id)], ['Name', (row) => row.name], ['FQDN', (row) => row.fqdn], ['Scheme', (row) => row.scheme], ['Memory', (row) => `${row.memory.toLocaleString()} MB`], ['Disk', (row) => `${row.disk.toLocaleString()} MB`], ['Daemon', (row) => `${row.daemon_listen} / SFTP ${row.daemon_sftp}`], ['State', (row) => row.maintenance_mode ? 'Maintenance' : row.public ? 'Public' : 'Private']];
const locationColumns: Column<Location>[] = [['ID', (row) => String(row.id)], ['Short', (row) => row.short], ['Long', (row) => row.long ?? ''], ['Created', (row) => formatDate(row.created_at)]];
const dbHostColumns: Column<DatabaseHost>[] = [['ID', (row) => String(row.id)], ['Name', (row) => row.name], ['Host', (row) => `${row.host}:${row.port}`], ['Username', (row) => row.username], ['Node', (row) => row.node == null ? 'Any' : String(row.node)], ['Max DBs', (row) => row.max_databases == null ? 'Unlimited' : String(row.max_databases)]];
const nestColumns: Column<Nest>[] = [['ID', (row) => String(row.id)], ['Name', (row) => row.name], ['Author', (row) => row.author], ['UUID', (row) => row.uuid], ['Description', (row) => row.description ?? '']];

function breadcrumb(route: Route, server?: PanelServer) {
  if (route.area === 'server') return `Servers / ${server?.identifier ?? route.serverId} / ${serverPageTitle(route.serverPage)}`;
  if (route.area === 'admin') return `Admin / ${adminTitle(route.adminPage)}`;
  if (route.area === 'account') return `Account / ${accountTitle(route.accountPage)}`;
  return 'Servers';
}

function serverPageTitle(page?: string) {
  const titles: Record<string, string> = {
    console: 'Console',
    files: 'File Manager',
    databases: 'Databases',
    schedules: 'Schedules',
    users: 'Users',
    backups: 'Backups',
    network: 'Network',
    startup: 'Startup',
    settings: 'Settings',
    activity: 'Activity'
  };
  return titles[page ?? 'console'] ?? 'Console';
}

function adminTitle(page?: string) {
  const titles: Record<string, string> = {
    overview: 'Overview',
    settings: 'Settings',
    arix: 'Arix Theme',
    'application-api': 'Application API',
    locations: 'Locations',
    nodes: 'Nodes',
    servers: 'Servers',
    users: 'Users',
    nests: 'Nests',
    mounts: 'Mounts',
    databases: 'Databases'
  };
  return titles[page ?? 'overview'] ?? 'Overview';
}

function accountTitle(page?: string) {
  const titles: Record<string, string> = {
    overview: 'Overview',
    api: 'API Credentials',
    ssh: 'SSH Keys',
    activity: 'Activity'
  };
  return titles[page ?? 'overview'] ?? 'Overview';
}

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatDuration(ms: number) {
  if (!Number.isFinite(ms) || ms <= 0) return '0m';
  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function formatDate(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

createRoot(document.getElementById('root')!).render(<App />);

function connectionAddress(allocation?: Allocation) { return allocation?.ip ? `${allocation.ip_alias || allocation.ip}:${allocation.port}` : "Not assigned"; }
